/**
 * Reading a registry record from the portal's own API.
 *
 * The server used to render these pages, calling `readRecord` against the chain
 * directly. It still does the chain read — the logic moved to
 * `server/internal/registry` — but the browser now asks for the result over
 * `/api/registry/:id` instead of receiving it as HTML.
 *
 * The three rules that governed the server reader still govern what comes back,
 * and they are worth repeating here because this is now where most people will
 * look:
 *
 *  1. `status` is `effectiveStatus`, not the stored field. A lapsed `expiresAt`
 *     writes no storage, so the raw field keeps claiming `Active` for a vendor
 *     whose certificate ran out months ago. `storedStatus` carries the raw one,
 *     for forensics.
 *  2. The registry is the source of truth, not the certificate NFT. A
 *     credential stays in its holder's wallet after revocation.
 *  3. `usable` already folds in the parent vendor's standing for an
 *     `ExternalUtility`. Do not reassemble that rule from `status`.
 */
import type { Address, Hex } from "viem";

import type { RegistryStatus, RegistryType, UtilityType } from "./status";

export type RegistryRecordView = {
  registryId: Hex;
  /** Canonical name when recognised; `null` for vendor ids, which is normal. */
  name: string | null;
  registryType: RegistryType;
  utilityType: UtilityType;
  /** The status to display. Already accounts for expiry. */
  status: RegistryStatus;
  /** The raw stored field, for forensics. May disagree with `status`. */
  storedStatus: RegistryStatus;
  usable: boolean;
  operator: Address | null;
  module: Address | null;
  permanentVault: Address | null;
  version: number;
  parentId: Hex | null;
  certificateTokenId: string | null;
  registeredAt: number;
  activatedAt: number;
  expiresAt: number;
  metadataHash: Hex;
  /** Raw ASETQU permanently locked in this record's vault, or null. */
  lockedBalanceRaw: string | null;
};

export type StatusPresentation = {
  label: string;
  tone: "good" | "warn" | "bad" | "neutral";
  /** Whether a user should be encouraged to transact with this record. */
  transactable: boolean;
  explanation: string;
};

export type RegistryRecordResponse = RegistryRecordView & {
  versions: number[];
  children: string[];
  presentation: StatusPresentation;
};

/**
 * Why a lookup produced no record.
 *
 * These are kept apart rather than collapsed into one error because they are
 * different answers to the visitor's actual question. "We cannot check" is not
 * "this is not official", and a page that renders the first as the second is
 * the specific failure the whole registry UI is built to avoid.
 */
export type RegistryLookupFailure =
  /** The id is not a canonical name or a 32-byte hash. Nothing to look up. */
  | { kind: "malformed"; message: string }
  /** No registry address on this deployment. 503, never 404. */
  | { kind: "unconfigured"; message: string }
  /** The chain could not be read. Transient, and must not read as "not official". */
  | { kind: "unreachable"; message: string };

export type RegistryLookup =
  | { ok: true; record: RegistryRecordResponse }
  | { ok: false; failure: RegistryLookupFailure };

export async function fetchRegistryRecord(
  registryId: string,
  options: { version?: number; signal?: AbortSignal } = {},
): Promise<RegistryLookup> {
  const url = new URL(`/api/registry/${encodeURIComponent(registryId)}`, window.location.origin);
  if (options.version !== undefined) {
    url.searchParams.set("version", String(options.version));
  }

  let response: Response;
  try {
    response = await fetch(url, { signal: options.signal });
  } catch (error) {
    return {
      ok: false,
      failure: {
        kind: "unreachable",
        message:
          error instanceof Error && error.name === "AbortError"
            ? "Permintaan dibatalkan."
            : "Portal tidak dapat dihubungi. Periksa koneksi lalu coba lagi.",
      },
    };
  }

  if (response.ok) {
    return { ok: true, record: (await response.json()) as RegistryRecordResponse };
  }

  const detail = await readError(response);

  if (response.status === 400) {
    return { ok: false, failure: { kind: "malformed", message: detail } };
  }
  if (response.status === 503) {
    return { ok: false, failure: { kind: "unconfigured", message: detail } };
  }
  return { ok: false, failure: { kind: "unreachable", message: detail } };
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.trim() !== "") return body.error;
  } catch {
    // Fall through to the generic message below.
  }
  return `Portal menjawab ${response.status}.`;
}
